import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Snackbar,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import TextFieldController from "../../../components/Controllers/TextFieldController";
import FormHelperTextWithLink from "../../../components/Controllers/FormHelperTextWithLink";
import StyledButton from "../../../components/CustomButtons/StyledButton";
import GenericAPI from "../../../services/api/GenericAPI";
import { CertQuerySchemaStep1, CertQuerySchemaStep2 } from "./validations";
import { formatRut, generateUrl, isDev } from "../../../Helpers/utils";
import Card from "../../../components/Card/Card";

const ROWS_PER_PAGE = 5;

const getErrorDescription = (code) => {
  switch (code) {
    case "network":
      return "Ha ocurrido un error. Verifique su conexión.";
    case "E01":
      return "El rut o correo ingresado no existe en nuestra base de datos, comuníquese con contacto@firmadox.cl.";
    case "E02":
      return "Ha ocurrido un error enviando el correo electrónico.";
    case "E07":
      return "El PIN ya no está disponible. Debe solicitar un nuevo PIN.";
    case "C01":
      return "El código no es válido.";
    default:
      return "No se pudo procesar la solicitud.";
  }
};

const CertificatesQueryForm = ({ step, handleChangeStep }) => {
  const [codeRefId, setCodeRefId] = useState(null);

  const [data, setData] = useState({
    certificates: [],
    email: null,
    fullname: null,
    rut: null,
  });
  const [page, setPage] = useState(0);

  const [isUIBusy, setUIBusy] = useState(false);

  const [errorCode, setErrorCode] = useState(null);
  const [message, setMessage] = useState(null);
  const [isMessageVisible, setMessageVisible] = useState(false);

  const {
    control: formControlStep1,
    handleSubmit: handleFormSubmitStep1,
    // getValues: getValuesStep1,
    setValue: setFormValueStep1,
    trigger: triggerFormStep1,
    formState: { errors: formErrorsStep1, isValid: isFormValidStep1 },
  } = useForm({
    defaultValues: {
      // rut: isDev() ? "55.555.555-5" : "",
      // email: isDev() ? "felix.prieto@dox.cl" : "",
      rut: "",
      email: "",
    },
    mode: "all", //  onChange | onBlur | onSubmit | onTouched | all
    resolver: yupResolver(CertQuerySchemaStep1),
  });

  const {
    control: formControlStep2,
    handleSubmit: handleFormSubmitStep2,
    setValue: setFormValueStep2,
    trigger: triggerFormStep2,
    getValues: getFormValuesStep2,
    getFieldState: getFormFieldStateStep2,
    setFocus: setFormFocusStep2,
    // watch: watchStep2,
    formState: { errors: formErrorsStep2, isValid: isFormValidStep2 },
  } = useForm({
    defaultValues: {
      email: "",
      // code: isDev() ? "12345" : "",
      code: "",
    },
    mode: "all", //  onChange | onBlur | onSubmit | onTouched | all
    resolver: yupResolver(CertQuerySchemaStep2),
  });

  useEffect(() => {
    if (errorCode) {
      setMessage({ text: getErrorDescription(errorCode), severity: "error" });
    }
  }, [errorCode, setMessage]);

  useEffect(() => {
    setMessageVisible(message != null);
  }, [message, setMessageVisible]);

  const handleSubmit = useCallback(
    async (data) => {
      // console.log(data);

      setUIBusy(true);
      setErrorCode(null);
      setMessage(null);

      switch (step) {
        case 1: // Datos Personales => Valida tu identidad
          let { rut, email: emailStep1 } = data;
          const paramsStep1 = { rut, email: emailStep1 };

          console.log("Params:", paramsStep1);

          GenericAPI.sendCode(paramsStep1)
            .then((value) => {
              console.log(value);

              const { refId, error } = value.payload;

              if (value.success) {
                setFormValueStep2("email", emailStep1, {
                  shouldValidate: false,
                  shouldDirty: false,
                });

                setCodeRefId(refId);

                setMessage({
                  text: "Correo enviado satisfactoriamente.",
                  severity: "success",
                });

                handleChangeStep();
              } else {
                setErrorCode(error); // code
              }
            })
            .catch((reason) => {
              console.error(reason);

              setErrorCode("network");
            })
            .finally(() => {
              setUIBusy(false);
            });

          break;
        case 2: // Valida tu identidad => Certificados
          let { code } = data;
          const paramsStep2 = { refId: codeRefId, code };

          console.log("Params:", paramsStep2);

          GenericAPI.validateCode(paramsStep2)
            .then((value) => {
              console.log(value);

              const { isValid, certificates, email, fullname, rut, error } =
                value.payload || {};

              if (value.success) {
                if (isValid) {
                  setCodeRefId(null);

                  setData({
                    certificates: certificates.map((value) => ({
                      id: value["refId"],
                      fecha: value["createdAt"],
                      tipo: value["certType"],
                      periodo: value["interval"],
                      estado: value["status"],
                      validez: value["validity"],
                    })),
                    email,
                    fullname,
                    rut,
                  });

                  handleChangeStep();
                } else {
                  setErrorCode("C01");
                }
              } else {
                setErrorCode(error); // code
              }
            })
            .catch((reason) => {
              console.error(reason);

              setErrorCode("network");
            })
            .finally(() => {
              setUIBusy(false);
            });

          break;
        default:
          console.error(`Step ${step} is not implemented`);
          return;
      }
    },
    [
      step,
      handleChangeStep,
      setUIBusy,
      setErrorCode,
      setData,
      setFormValueStep2,
      codeRefId,
      setCodeRefId,
      setMessage,
    ]
  );

  const handleResend = useCallback(
    (evt) => {
      // console.log("handleResend");
      // evt.preventDefault();

      triggerFormStep2("email").then((r) => {
        setUIBusy(true);
        setErrorCode(null);
        setMessage(null);

        const email = getFormValuesStep2("email");
        const params = { refId: codeRefId, email };

        console.log("Params:", params);

        GenericAPI.resendCode(params)
          .then((value) => {
            console.log(value);

            const { refId, error } = value.payload;

            if (value.success) {
              setFormFocusStep2("code");

              if (codeRefId !== refId) {
                setCodeRefId(refId);
              }

              setMessage({
                text: "Correo enviado satisfactoriamente.",
                severity: "success",
              });
            } else {
              setFormFocusStep2("email");

              setErrorCode(error); // code
            }
          })
          .catch((reason) => {
            console.error(reason);

            setErrorCode("network");
          })
          .finally(() => {
            setUIBusy(false);
          });
      });
    },
    [
      triggerFormStep2,
      setUIBusy,
      getFormValuesStep2,
      setErrorCode,
      setFormFocusStep2,
      codeRefId,
      setCodeRefId,
      setMessage,
    ]
  );

  const handleChangePage = useCallback((event, newPage) => setPage(newPage), [
    setPage,
  ]);

  const getStepTitle = useCallback(
    (step) => {
      switch (step) {
        case 1: // Datos Personales
          return (
            <Typography style={{ fontSize: "19px", marginTop: 18 }}>
              Ingresa los datos personales
            </Typography>
          );
        case 2: // Valida tu identidad
          return (
            <>
              <Typography style={{ fontSize: "19px", marginTop: 18 }}>
                Valida tu identidad
              </Typography>
              <Typography style={{ fontSize: "15px", marginTop: 9 }}>
                Inserte el código enviado a su correo electrónico.
              </Typography>
            </>
          );
        case 3: // Certificados
          return (
            <>
              <Grid
                container
                direction={"row"}
                spacing={2}
                style={{ padding: 20 }}
                justify={"center"}
              >
                <Grid item>
                  <Grid container spacing={1}>
                    <Grid item>
                      <Typography
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        RUT:
                      </Typography>
                    </Grid>

                    <Grid item>
                      <Typography style={{ fontSize: "16px" }}>
                        {data.rut}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item>
                  <Grid container spacing={1}>
                    <Grid item>
                      <Typography
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        Correo electrónico:
                      </Typography>
                    </Grid>

                    <Grid item>
                      <Typography style={{ fontSize: "16px" }}>
                        {data.email}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item>
                  <Grid container spacing={1}>
                    <Grid item>
                      <Typography
                        style={{ fontSize: "16px", fontWeight: "bold" }}
                      >
                        Nombre:
                      </Typography>
                    </Grid>

                    <Grid item>
                      <Typography style={{ fontSize: "16px" }}>
                        {data.fullname}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Typography style={{ fontSize: "19px", marginTop: 5 }}>
                Certificados
              </Typography>
            </>
          );
        default:
          return "Not implemented";
      }
    },
    [data]
  );

  const getStepContent = useCallback(
    (step) => {
      switch (step) {
        case 1: // Datos Personales
          return (
            <Grid
              key={1}
              container
              component={"form"}
              onSubmit={handleFormSubmitStep1(handleSubmit)}
              style={{ flexDirection: "column" }}
              spacing={2}
            >
              <Grid item>
                <TextFieldController
                  control={formControlStep1}
                  label={"RUT"}
                  name={"rut"}
                  errors={formErrorsStep1}
                  autoComplete="off"
                  disabled={isUIBusy}
                  autoFocus={true}
                  required={true}
                  inputProps={{
                    maxLength: 12,
                    style: { textTransform: "uppercase" },
                  }}
                  onChange={(value) => {
                    setFormValueStep1(
                      "rut",
                      formatRut(
                        value.replace(/[^0-9kK-]/g, "").toUpperCase(),
                        true
                      ),
                      { shouldValidate: true }
                    );
                  }}
                />
              </Grid>

              <Grid item>
                <TextFieldController
                  control={formControlStep1}
                  label={"Correo Electrónico"}
                  name={"email"}
                  errors={formErrorsStep1}
                  autoComplete="off"
                  required={true}
                  disabled={isUIBusy}
                  inputProps={{
                    style: { textTransform: "uppercase" },
                  }}
                />
              </Grid>

              <Grid item>
                <Grid container justify={"flex-end"}>
                  <StyledButton
                    type="submit"
                    disabled={isUIBusy || !isFormValidStep1}
                  >
                    Solicitar PIN
                  </StyledButton>
                </Grid>
              </Grid>
            </Grid>
          );
        case 2: // Valida tu identidad
          return (
            <Grid
              key={2}
              container
              component={"form"}
              onSubmit={handleFormSubmitStep2(handleSubmit)}
              style={{ display: "flex", flexDirection: "column" }}
              spacing={2}
            >
              <Grid item>
                <TextFieldController
                  control={formControlStep2}
                  label={"Correo Electrónico"}
                  name={"email"}
                  errors={formErrorsStep2}
                  autoComplete="off"
                  disabled={isUIBusy}
                  required={true}
                  inputProps={{
                    style: { textTransform: "uppercase" },
                  }}
                  FormHelperTextProps={{
                    component: FormHelperTextWithLink,
                    linkText: "Reenviar Correo",
                    onClick: handleResend,
                    isDisabled:
                      isUIBusy ||
                      (formErrorsStep2 &&
                        formErrorsStep2.hasOwnProperty("email")),
                  }}
                />
              </Grid>

              <Grid item>
                <TextFieldController
                  control={formControlStep2}
                  label={"Código"}
                  name={"code"}
                  errors={formErrorsStep2}
                  autoComplete="off"
                  disabled={isUIBusy}
                  autoFocus={true}
                  required={true}
                  inputProps={{
                    maxLength: 5,
                  }}
                />
              </Grid>

              <Grid item>
                <Grid container justify={"flex-end"}>
                  <StyledButton
                    variant={"outlined"}
                    type="submit"
                    disabled={isUIBusy || !isFormValidStep2}
                  >
                    Validar PIN
                  </StyledButton>
                </Grid>
              </Grid>
            </Grid>
          );
        case 3: //Certificados
          return data.certificates.length === 0 ? (
            <Box justifyContent={"center"} display={"flex"}>
              <Typography
                style={{ fontSize: "16px", marginTop: 20, marginBottom: 30 }}
                color={"error"}
              >
                No existen certificados para mostrar
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ paddingLeft: 30 }}>
                        Fecha Solicitud
                      </TableCell>
                      <TableCell>Tipo Certificado</TableCell>
                      <TableCell>Período</TableCell>
                      <TableCell width={140}>Estado</TableCell>
                      <TableCell width={150}>Vigencia</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.certificates
                      .slice(
                        page * ROWS_PER_PAGE,
                        page * ROWS_PER_PAGE + ROWS_PER_PAGE
                      )
                      .map((row) => (
                        <TableRow key={row.id}>
                          <TableCell
                            component="th"
                            scope="row"
                            style={{ paddingLeft: 30 }}
                          >
                            {row.fecha}
                          </TableCell>
                          <TableCell>{row.tipo}</TableCell>
                          <TableCell>{row.periodo}</TableCell>
                          <TableCell>{row.estado}</TableCell>
                          <TableCell>
                            {row.validez.toLowerCase() !== "no vigente" ? (
                              row.validez
                            ) : (
                              <a
                                href={generateUrl(
                                  row.tipo.toLowerCase().indexOf("simple") !==
                                    -1
                                    ? "compra/firma-simple"
                                    : "compra/firma-token"
                                )}
                                target={"_blank"}
                                title={row.validez}
                                style={{
                                  color: "rgb(161, 195, 48)",
                                  textDecoration: "none",
                                }}
                              >
                                {row.validez}
                              </a>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>

                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        // rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                        rowsPerPageOptions={[]}
                        count={data.certificates.length}
                        rowsPerPage={ROWS_PER_PAGE}
                        page={page}
                        onChangePage={handleChangePage}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </>
          );
        default:
          return "Not implemented";
      }
    },
    [
      data,
      page,
      setPage,
      ROWS_PER_PAGE,
      isUIBusy,
      formControlStep1,
      formControlStep2,
      formErrorsStep1,
      formErrorsStep2,
      isFormValidStep1,
      isFormValidStep2,
      handleFormSubmitStep1,
      handleFormSubmitStep2,
      getFormFieldStateStep2,
      triggerFormStep2,
      handleSubmit,
      setFormValueStep1,
    ]
  );

  return (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Card
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          marginBottom: 0,
        }}
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          style={{ width: step !== 3 ? 500 : "100%" }}
        >
          {getStepTitle(step)}
          <Box
            style={{
              padding: step !== 3 ? "20px 48px" : "20px 0 0",
              width: "100%",
            }}
          >
            {getStepContent(step)}
          </Box>

          {/*{isDev() && (*/}
          {/*  <div>*/}
          {/*    {JSON.stringify({*/}
          {/*      codeRefId,*/}
          {/*      errorCode,*/}
          {/*      message,*/}
          {/*      isMessageVisible,*/}
          {/*      formErrorsStep1,*/}
          {/*      formErrorsStep2,*/}
          {/*      isFormValidStep1,*/}
          {/*      isFormValidStep2,*/}
          {/*      isUIBusy,*/}
          {/*    })}*/}
          {/*  </div>*/}
          {/*)}*/}
        </Box>
      </Card>

      <Box>
        <Snackbar
          open={isMessageVisible}
          onClose={() => setMessageVisible(false)}
          autoHideDuration={9000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          style={{ position: "relative", marginTop: -10 }}
        >
          {message !== null && (
            <Alert
              elevation={0}
              variant="filled"
              severity={message.severity}
              style={{ width: "100%" }}
            >
              <div>{message.text}</div>
            </Alert>
          )}
        </Snackbar>
      </Box>
    </Box>
  );
};

export default CertificatesQueryForm;
